// PE-21 — GET /me/programs/:id already carries a server-computed `warnings`
// field (muscle-adjacency advisory, apps/api/src/lib/programWarnings.ts) on
// every read/write response, but nothing in the client ever read it: built,
// shipped, unreachable (see ProgramEditor/types.ts's ProgramApiFetch doc
// comment). Mirrors fetchMaxesMap (maxes.ts) — same "fetch one extra
// advisory thing, best-effort" shape, kept separate because muscle-adjacency
// warnings aren't part of the Max domain.
//
// `warnings` isn't part of the Program contract clients zod-validate against
// (same doc comment: "clients that zod-parse Program strip it") — it's
// advisory and non-authoritative, so a structural check is proportionate
// here rather than a full zod schema for a plain TS interface
// (MuscleAdjacencyWarning, muscleAdjacency.ts, has none).

import type { ApiGet } from "./apiFetch.js";
import type { MuscleAdjacencyWarning } from "./muscleAdjacency.js";

const isMuscleAdjacencyWarning = (v: unknown): v is MuscleAdjacencyWarning =>
  typeof v === "object" &&
  v !== null &&
  typeof (v as { muscleSlug?: unknown }).muscleSlug === "string" &&
  Array.isArray((v as { adjacentPairs?: unknown }).adjacentPairs);

/**
 * Fetches `/api/me/programs/:id` and returns just its `warnings`. Rows that
 * don't structurally look like a MuscleAdjacencyWarning are dropped rather
 * than trusted — same skip-on-drift posture fetchMaxesMap takes on a bad Max
 * row, so one malformed entry can't blow up the whole advisory list.
 */
export const fetchProgramWarnings = async (
  apiFetch: ApiGet,
  programId: string,
): Promise<MuscleAdjacencyWarning[]> => {
  const res = await apiFetch<{ warnings?: unknown }>(
    `/api/me/programs/${encodeURIComponent(programId)}`,
  );
  const raw = Array.isArray(res?.warnings) ? res.warnings : [];
  return raw.filter(isMuscleAdjacencyWarning);
};
